import { useEffect } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Helmet } from "react-helmet-async";
import { MapPin, Mail, Phone, Clock, MessageSquare } from "lucide-react";
import { GenieAvatar } from "@/components/GenieAvatar";
import { getCanonicalUrl } from "@/config/site";
import { createContactPageSchema, createOrganizationSchema } from "@/config/seo";
import HomeNav from "./HomeNav";
import HomeFooter from "./HomeFooter";
import GenieEmbed from "@/components/widget/GenieEmbed";
import { trackCallClick } from "@/lib/ga4";

const HUBSPOT_PORTAL_ID = "441784197";
const HUBSPOT_FORM_ID = "143814c5-72da-4213-951f-b4ce677b7ce6";
const HUBSPOT_REGION = "ap1";

const ContactPage = () => {
  const description =
    "Contact Help Genie to ask questions, request a walkthrough, discuss pricing or get help planning your voice AI deployment.";
  const contactSchema = createContactPageSchema();
  const organizationSchema = createOrganizationSchema();

  useEffect(() => {
    // Load HubSpot forms script
    const script = document.createElement("script");
    script.src = `https://js-${HUBSPOT_REGION}.hsforms.net/forms/embed/v2.js`;
    script.async = true;
    script.onload = () => {
      // @ts-expect-error - HubSpot global
      if (window.hbspt) {
        // @ts-expect-error - HubSpot global
        window.hbspt.forms.create({
          region: HUBSPOT_REGION,
          portalId: HUBSPOT_PORTAL_ID,
          formId: HUBSPOT_FORM_ID,
          target: "#hubspot-form-container",
        });
      }
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      document.body.removeChild(script);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Contact Help Genie | Book a Demo or Ask a Question</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={getCanonicalUrl("/contact")} />
        <meta property="og:title" content="Contact Help Genie | Book a Demo or Ask a Question" />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={getCanonicalUrl("/contact")} />
        <meta property="og:image" content={getCanonicalUrl("/helpgenie_logo.png")} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Help Genie | Book a Demo or Ask a Question" />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={getCanonicalUrl("/helpgenie_logo.png")} />
        <script type="application/ld+json">{JSON.stringify(contactSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>

      <div className="min-h-screen w-full flex flex-col bg-[#F9FAFC]">
        <HomeNav />

      {/* Hero Section */}
      <section className="relative w-full pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="/insights-background.webp" alt="Contact background" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/80 via-purple-900/80 to-pink-800/80"></div>
        </div>

        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-8 text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">Get in Touch</h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            Have questions about Help Genie? We'd love to hear from you. Reach out and let's start a conversation.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-3 text-sm">
            <a href="/pricing" className="text-gray-100 hover:text-white transition-colors">
              Review pricing
            </a>
            <a href="/help-genie-for-business-solutions" className="text-gray-100 hover:text-white transition-colors">
              Explore solutions
            </a>
            <a href="/playbooks" className="text-gray-100 hover:text-white transition-colors">
              Browse playbooks
            </a>
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="w-full py-20 bg-white">
        <div className="w-full max-w-screen-xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Form */}
            <div className="order-2 lg:order-1">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
              <p className="text-gray-600 mb-8">
                Fill out the form below and we'll get back to you as soon as possible. We typically respond within 24 hours.
              </p>

              {/* HubSpot Form Container */}
              <div
                id="hubspot-form-container"
                className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-200 min-h-[400px]"
              />
            </div>

            {/* Contact Information */}
            <div className="order-1 lg:order-2">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Contact Information</h2>
              <p className="text-gray-600 mb-8">
                Prefer to reach out directly? Here's how you can get in touch with our team.
              </p>

              <div className="space-y-6">
                {/* Email */}
                <div className="flex items-start gap-4 p-5 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email Us</h3>
                    <CopyableEmail
                      email="contact@helpgenie.ai"
                      href="mailto:contact@helpgenie.ai"
                      className="text-genie-primary font-medium"
                    />
                    <p className="text-sm text-gray-500 mt-1">We'll respond within 24 hours</p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4 p-5 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Call Us</h3>
                    <a
                      href="tel:+6421959509"
                      onClick={() => trackCallClick("contact_page")}
                      className="text-purple-600 hover:text-purple-700 transition-colors font-medium"
                    >
                      +64 21 95 95 09
                    </a>
                    <p className="text-sm text-gray-500 mt-1">New Zealand business hours</p>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start gap-4 p-5 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Headquarters</h3>
                    <p className="text-gray-700">Auckland, New Zealand</p>
                    <p className="text-sm text-gray-500 mt-1">Serving customers worldwide</p>
                  </div>
                </div>

                {/* Business Hours */}
                <div className="flex items-start gap-4 p-5 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Business Hours</h3>
                    <p className="text-gray-700">Monday - Friday: 9am - 5pm NZST</p>
                    <p className="text-sm text-gray-500 mt-1">Our Genies are available 24/7</p>
                  </div>
                </div>
              </div>

              {/* Talk to Genie CTA */}
              <div className="mt-8 p-6 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl text-white">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Need Instant Help?</h3>
                    <p className="text-purple-100 text-sm mb-3">
                      Click the Genie button in the corner to speak with our Genie. Available 24/7 to answer your
                      questions about Help Genie.
                    </p>
                    <p className="text-xs text-purple-200">Try saying: "Tell me about Help Genie pricing"</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="w-full py-20 bg-gray-50">
        <div className="w-full max-w-screen-xl mx-auto px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Quick answers to common questions. Can't find what you're looking for? Reach out to us directly.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="font-semibold text-gray-900 mb-2">How quickly can I get started?</h3>
              <p className="text-gray-600 text-sm">
                You can create your first Genie in minutes with our free plan. For professional setups with custom
                configuration, our onboarding typically takes 1-2 business days.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="font-semibold text-gray-900 mb-2">Do you offer demos?</h3>
              <p className="text-gray-600 text-sm">
                Absolutely! You can try our interactive demo right on this page using the Genie button, or schedule a
                personalized walkthrough with our team.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="font-semibold text-gray-900 mb-2">What languages do you support?</h3>
              <p className="text-gray-600 text-sm">
                Help Genie supports multiple languages including English, Spanish, French, German, and many more. Contact
                us for specific language requirements.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="font-semibold text-gray-900 mb-2">Can I get a phone number in my country?</h3>
              <p className="text-gray-600 text-sm">
                We offer phone numbers in the US, Canada, UK, Australia, New Zealand, and many other countries. Pricing
                varies by region.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section with Genie */}
      <section className="w-full py-16 md:py-24 bg-white">
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-center">
          <div className="md:w-1/2 text-center md:text-left mb-12 md:mb-0 md:pr-6 lg:pr-12">
            <div className="max-w-lg mx-auto md:mx-0 md:ml-auto">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-indigo-900 mb-4">
                Ready to Elevate Your Customer Experience?
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Give your customers instant voice support that's always available, always helpful
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <a
                  href="/signup"
                  className="inline-block px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 text-lg"
                >
                  Start Free Today
                </a>
                <a
                  href="/live-agent/business-onboarding"
                  className="inline-block px-8 py-3 bg-white border-2 border-gray-300 text-gray-900 font-semibold rounded-lg hover:bg-gray-50 transition-all duration-300 text-lg"
                >
                  Talk to Sales
                </a>
              </div>
            </div>
          </div>

          <div className="md:w-1/2 flex justify-center items-center">
            <div className="max-w-xs mx-auto">
              <div className="transform block scale-75 lg:scale-90">
                <GenieAvatar
                  speaking={true}
                  color="#9333ea"
                  secondaryColor="#2563eb"
                  gradientEnabled={true}
                  gradientAngle={135}
                  customAvatarUrl="/genie-mascots/role/concierge.png"
                  size={320}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

        <HomeFooter />
        <GenieEmbed position="bottom-right" />
      </div>
    </>
  );
};

export default ContactPage;
